import { useState, useRef, useEffect } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import { useFlipStorage } from "../../../contexts/FlipStorageContext";

const STATUS_STYLES: Record<string, { dark: string; light: string; label: string }> = {
  sold: { dark: "bg-emerald-500/12 text-emerald-400", light: "bg-emerald-50 text-emerald-700", label: "Sold" },
  listed: { dark: "bg-amber-500/12 text-amber-400", light: "bg-amber-50 text-amber-700", label: "Listed" },
  in_hand: { dark: "bg-white/8 text-white/50", light: "bg-black/5 text-[#888]", label: "In Hand" },
};

const PLATFORMS = [
  "All",
  "StockX",
  "GOAT",
  "eBay",
  "Depop",
  "Grailed",
  "Poshmark",
  "Mercari",
  "Whatnot",
  "Amazon",
  "OfferUp",
  "Goldin",
  "Other",
];

const STATUS_OPTIONS = [
  { value: "All", label: "All Statuses" },
  { value: "sold", label: "Sold" },
  { value: "listed", label: "Listed" },
  { value: "in_hand", label: "In Hand" },
];

interface CustomSelectProps {
  value: string;
  onChange: (val: string) => void;
  options: { value: string; label: string }[];
  isDark: boolean;
}

function CustomSelect({ value, onChange, options, isDark }: CustomSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={`font-inter text-xs px-3 py-2 rounded-lg border cursor-pointer focus:outline-none flex items-center gap-2 whitespace-nowrap transition-colors ${
          isDark
            ? "bg-white/5 border-white/10 text-white/70 hover:bg-white/8"
            : "bg-white border-black/10 text-[#555] hover:bg-black/3"
        }`}
      >
        {selected?.label ?? value}
        <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className={`absolute top-full left-0 mt-1 rounded-xl border z-50 py-1 overflow-hidden ${
            isDark ? "bg-[#141414] border-white/10" : "bg-white border-black/8"
          }`}
          style={{ minWidth: "140px" }}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left font-inter text-xs px-3 py-2 flex items-center justify-between gap-3 cursor-pointer transition-colors ${
                opt.value === value
                  ? isDark ? "bg-white/8 text-white" : "bg-black/5 text-[#111]"
                  : isDark ? "text-[#aaa] hover:bg-white/6 hover:text-white" : "text-[#444] hover:bg-black/4 hover:text-[#111]"
              }`}
            >
              {opt.label}
              {opt.value === value && <i className={`ri-check-line text-xs ${isDark ? "text-white" : "text-[#111]"}`} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FlipHistory() {
  const { isDark } = useTheme();
  const { entries, deleteEntry } = useFlipStorage();
  const [filterPlatform, setFilterPlatform] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [sortBy, setSortBy] = useState<"date" | "profit">("date");
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const filtered = entries
    .filter((f) => filterPlatform === "All" || f.platform === filterPlatform)
    .filter((f) => filterStatus === "All" || f.status === filterStatus)
    .sort((a, b) => {
      if (sortBy === "profit") return b.profit - a.profit;
      return b.id - a.id;
    });

  const totalProfit = filtered.filter((f) => f.status === "sold").reduce((sum, f) => sum + f.profit, 0);
  const soldCount = filtered.filter((f) => f.status === "sold").length;

  const platformOptions = PLATFORMS.map((p) => ({ value: p, label: p === "All" ? "All Platforms" : p }));

  return (
    <div>
      {/* Summary bar */}
      <div className={`rounded-2xl p-5 border mb-4 flex flex-wrap items-center justify-between gap-4 ${isDark ? "bg-[#0d0d0d] border-white/6" : "bg-[#f8f8f8] border-black/6"}`}>
        <div className="flex items-center gap-6">
          <div>
            <p className={`font-inter text-[10px] uppercase tracking-widest mb-0.5 ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>Profit (filtered)</p>
            <p className={`font-syne font-bold text-xl ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
              +${totalProfit.toLocaleString()}
            </p>
          </div>
          <div className={`w-px h-10 ${isDark ? "bg-white/6" : "bg-black/6"}`} />
          <div>
            <p className={`font-inter text-[10px] uppercase tracking-widest mb-0.5 ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>Items sold</p>
            <p className={`font-syne font-bold text-xl ${isDark ? "text-white" : "text-[#111]"}`}>{soldCount}</p>
          </div>
          <div className={`w-px h-10 ${isDark ? "bg-white/6" : "bg-black/6"}`} />
          <div>
            <p className={`font-inter text-[10px] uppercase tracking-widest mb-0.5 ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>Showing</p>
            <p className={`font-syne font-bold text-xl ${isDark ? "text-white" : "text-[#111]"}`}>{filtered.length}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <CustomSelect value={filterPlatform} onChange={setFilterPlatform} options={platformOptions} isDark={isDark} />
          <CustomSelect value={filterStatus} onChange={setFilterStatus} options={STATUS_OPTIONS} isDark={isDark} />
          <div className={`flex rounded-lg border overflow-hidden ${isDark ? "border-white/10" : "border-black/10"}`}>
            {(["date", "profit"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSortBy(s)}
                className={`font-inter text-xs px-3 py-2 cursor-pointer whitespace-nowrap transition-colors ${
                  sortBy === s
                    ? isDark ? "bg-white text-black" : "bg-[#111] text-white"
                    : isDark ? "text-[#555] hover:text-white" : "text-[#aaa] hover:text-[#111]"
                }`}
              >
                {s === "date" ? "Latest" : "Top Profit"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Empty state */}
      {entries.length === 0 ? (
        <div className={`rounded-2xl border-2 border-dashed flex flex-col items-center justify-center py-20 gap-3 ${isDark ? "border-white/6" : "border-black/6"}`}>
          <i className={`ri-history-line text-3xl ${isDark ? "text-white/15" : "text-black/15"}`} />
          <p className={`font-syne font-semibold text-base ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>No flips saved yet</p>
          <p className={`font-inter text-xs ${isDark ? "text-[#333]" : "text-[#ccc]"}`}>Use the Profit Calculator and hit &ldquo;Save to History&rdquo; to get started</p>
        </div>
      ) : (
        <div className={`rounded-2xl border overflow-hidden ${isDark ? "bg-[#0d0d0d] border-white/6" : "bg-[#f8f8f8] border-black/6"}`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${isDark ? "border-white/5" : "border-black/5"}`}>
                  {["Item", "Platform", "Date", "Buy", "Sell", "Fees", "Profit", "Status", ""].map((h) => (
                    <th key={h} className={`font-inter text-[10px] uppercase tracking-widest font-medium text-left px-5 py-3 ${isDark ? "text-[#3a3a3a]" : "text-[#bbb]"}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((flip, i) => {
                  const statusStyle = STATUS_STYLES[flip.status] ?? STATUS_STYLES.sold;
                  return (
                    <tr
                      key={flip.id}
                      className={`border-b transition-colors duration-100 ${isDark ? `border-white/3 ${i % 2 === 0 ? "" : "bg-white/1"} hover:bg-white/3` : `border-black/3 ${i % 2 === 0 ? "" : "bg-black/1"} hover:bg-black/3`}`}
                    >
                      <td className="px-5 py-3.5 max-w-48">
                        <p className={`font-inter text-xs font-medium leading-snug line-clamp-2 ${isDark ? "text-white/80" : "text-[#222]"}`}>{flip.item}</p>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`font-inter text-[11px] font-medium ${isDark ? "text-[#555]" : "text-[#999]"}`}>{flip.platform}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`font-inter text-[11px] whitespace-nowrap ${isDark ? "text-[#444]" : "text-[#aaa]"}`}>{flip.date}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#888]"}`}>${flip.buyPrice}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`font-inter text-xs ${flip.sellPrice > 0 ? isDark ? "text-white/70" : "text-[#333]" : isDark ? "text-[#3a3a3a]" : "text-[#ddd]"}`}>
                          {flip.sellPrice > 0 ? `$${flip.sellPrice}` : "—"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`font-inter text-xs ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>
                          {flip.fees > 0 ? `-$${flip.fees}` : "—"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`font-inter text-xs font-semibold ${flip.profit > 0 ? isDark ? "text-emerald-400" : "text-emerald-600" : isDark ? "text-[#3a3a3a]" : "text-[#ccc]"}`}>
                          {flip.profit > 0 ? `+$${flip.profit}` : "—"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`font-inter text-[10px] font-medium px-2 py-0.5 rounded-full ${isDark ? statusStyle.dark : statusStyle.light}`}>
                          {statusStyle.label}
                        </span>
                      </td>
                      <td className="px-3 py-3.5">
                        {confirmDelete === flip.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => { deleteEntry(flip.id); setConfirmDelete(null); }}
                              className="font-inter text-[10px] px-2 py-1 rounded-lg bg-red-500/15 text-red-400 hover:bg-red-500/25 cursor-pointer whitespace-nowrap transition-colors"
                            >
                              Delete
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmDelete(null)}
                              className={`font-inter text-[10px] px-2 py-1 rounded-lg cursor-pointer whitespace-nowrap transition-colors ${isDark ? "text-[#555] hover:text-white" : "text-[#bbb] hover:text-[#555]"}`}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmDelete(flip.id)}
                            className={`w-6 h-6 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${isDark ? "text-[#333] hover:text-red-400 hover:bg-red-500/10" : "text-[#ddd] hover:text-red-500 hover:bg-red-50"}`}
                          >
                            <i className="ri-delete-bin-line text-xs" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
